import { useEffect, useLayoutEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { authorize, clearUser, setUser, checkLicensePlateHash } from "../redux/ducks/user";
import { ReducersTypes } from "../redux/reducers/Models";
import IndexView from "./view";
import { auth, setCustomer } from "@/redux/ducks/customer";
import { getCustomerParameters } from "@/settings/api";
import useQuery from "@/hooks/useQuery";
import useCaptchaEnterprise from "@/hooks/useCaptchaEnterprise";
import useCaptcha from "@/hooks/useCaptcha";
import { useSingleFlag } from "@/hooks/useFlags";
import { useIsWebView } from "@/hooks/useIsWebView";
import { validateEmail, validatePhone } from "@/utils/functions";
import { setThemeInStorage } from "@/contexts/ThemeContext";

export default function Index() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const params = useQuery();
  const user = useSelector((state: ReducersTypes) => state.userReducer);
  const { isEnabled: isCaptchaMtcCaptcha, isLoading: isLoadingCaptchaFlag } = useSingleFlag('PD_CAPTCHA_MTCAPTCHA');
  const isWebView = useIsWebView();

  const [isVisibleMenu, setIsVisibleMenu] = useState(false);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [isVisibleModalLogin, setIsVisibleModalLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAcceptedTerms, setIsAcceptedTerms] = useState(false);
  const [error, setError] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [skipCaptchaValidation, setSkipCaptchaValidation] = useState(false);
  const [isParametersLoaded, setIsParametersLoaded] = useState(false);
  const lastProcessedHashFromUrl = useRef<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; phone?: string }>({});

  const captchaName = "mtcaptcha-intern";
  const shouldUseCaptcha = isParametersLoaded && !skipCaptchaValidation;


  useCaptcha({
    setIsVerified: isCaptchaMtcCaptcha ? setIsVerified : () => {},
    captchaName,
    enabled: shouldUseCaptcha && isCaptchaMtcCaptcha,
  });

  useCaptchaEnterprise({
    setIsVerified: !isCaptchaMtcCaptcha ? setIsCaptchaVerified : () => {},
    captchaName: "home-captcha",
    action: "login",
    enabled: shouldUseCaptcha && !isCaptchaMtcCaptcha,
  });

  useEffect(() => {
    const loadCustomerParameters = async () => {
      try {
        const parameters = await getCustomerParameters();
        setSkipCaptchaValidation(Boolean(parameters?.skipCaptchaValidation));
      } catch (_error) {
        setSkipCaptchaValidation(false);
      } finally {
        setIsParametersLoaded(true);
      }
    };

    loadCustomerParameters();
  }, []);

  useEffect(() => {
    window.__mundialeChatEnabled = true;
    window.dispatchEvent(new Event('mundiale:locationchange'));

    return () => {
      window.__mundialeChatEnabled = false;
      window.dispatchEvent(new Event('mundiale:locationchange'));
    };
  }, []);

  useEffect(() => {
    if (isCaptchaMtcCaptcha) {
      // @ts-expect-error
      const status = window?.mtcaptcha?.getStatus?.(captchaName);
      // @ts-expect-error
      const token = window?.mtcaptcha?.getVerifiedToken?.(captchaName);
      
      if (status?.isVerified && token) {
        setIsVerified(true);
      } else {
        setIsVerified(false);
      }
    }
  }, [location.pathname, isCaptchaMtcCaptcha, captchaName]);

  // Detectar WebAccessToken na URL e autenticar sem abrir LoginModal
  useEffect(() => {
    const webAccessToken = params.get('WebAccessToken') ?? params.get('webAccessToken');
    if (!webAccessToken) {
      return;
    }

    setIsLoading(true);

    const handleWebAccessTokenAuth = (success: boolean) => {
      setIsLoading(false);
      window.history.replaceState({}, document.title, window.location.pathname);

      if (!success) {
        setIsVisibleModalLogin(true);
      }
    };

    dispatch(auth({
      webAccessToken,
      callback: handleWebAccessTokenAuth,
    }));
  }, []);

  const toggleMenu = () => setIsVisibleMenu(isVisibleMenu => !isVisibleMenu);
  const toggleModal = () => setIsVisibleModal(isVisibleModal => !isVisibleModal);
  const toggleModalLogin = () => setIsVisibleModalLogin(isVisibleModalLogin => !isVisibleModalLogin);

  const goToCreateAccount = () => navigate("/register");

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'plate') {
      const cleanPlate = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
      dispatch(setUser({ [name]: cleanPlate }));
    } else {
      dispatch(setUser({ [name]: value }));
    }

    if (name === 'email') {
      setFieldErrors(prev => ({
        ...prev,
        email: value ? (validateEmail(value) ? undefined : 'E-mail inválido') : undefined
      }));
    } else if (name === 'phone') {
      setFieldErrors(prev => ({
        ...prev,
        phone: value ? (validatePhone(value) ? undefined : 'Celular inválido') : undefined
      }));
    }
  }

  const getIsValidForm = () => {
    const isCaptchaValid = skipCaptchaValidation ? true : (isCaptchaMtcCaptcha ? isVerified : isCaptchaVerified);
    const cleanPlate = (user?.plate as string)?.replace(/[^A-Z0-9]/g, "") || "";
    const hasValidEmail = validateEmail(user?.email as string);
    const hasValidPhone = validatePhone(user?.phone as string);
    return cleanPlate.length >= 5 && isCaptchaValid && hasValidEmail && hasValidPhone;
  };

  const resetCaptcha = () => {
    if (skipCaptchaValidation) {
      return;
    }

    if (isCaptchaMtcCaptcha) {
      // @ts-expect-error
      window?.mtcaptcha?.resetUI(captchaName);
      setIsVerified(false);
    } else {
      if (typeof window !== "undefined" && (window as any).grecaptcha?.reset) {
        (window as any).grecaptcha.reset();
      }
      setIsCaptchaVerified(false);
    }
  };

  const handleAuthorize = (success: boolean, errorMessage?: string) => {
    setIsLoading(false);

    if (!success) {
      if (errorMessage === "timeout") setError(true);
      toggleModal();
      resetCaptcha();
      return;
    }

    navigate("/home");
  }

  const handleAuthorizeFromUrl = (success: boolean, errorMessage?: string) => {
    setIsLoading(false);

    if (!success) {
      if (errorMessage === "timeout") setError(true);
      toggleModal();
      resetCaptcha();
      return;
    }

    const hash = location.hash;
    const isSigaFacilRoute = hash && hash.includes('#siga-facil');
    const isZapayRoute = hash && hash.includes('#zapay');

    if (!isSigaFacilRoute && !isZapayRoute) {
      navigate("/home");
      return;
    }

    navigate("/home");
  }

  const searchPlateFromUrl = async (plateFromUrl: string) => {
    const cleanPlate = plateFromUrl.toUpperCase().replace(/[^A-Z0-9]/g, "");
    
    if (!cleanPlate || cleanPlate.length < 5) {
      return;
    }

    dispatch(setUser({ 
      plate: cleanPlate,
      debts: [],
      cart: undefined,
      requestId: undefined,
    }));
    dispatch(setCustomer({
      debts: undefined,
      cart: undefined,
      requestId: undefined,
    }));
    
    setIsAcceptedTerms(true);
    setIsVerified(true);
    setIsCaptchaVerified(true);
    setIsLoading(true);

    try {
      dispatch(
        authorize({
          plate: cleanPlate,
          token: '',
          callback: handleAuthorizeFromUrl,
          isAcceptedTerms: true,
        })
      );
    } catch (error) {
      setIsLoading(false);
    }
  }

  const goToDebts = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();

    const emailVal = (user?.email as string) || '';
    const phoneVal = (user?.phone as string) || '';
    const submitErrors: { email?: string; phone?: string } = {};

    if (!emailVal) {
      submitErrors.email = 'E-mail obrigatório';
    } else if (!validateEmail(emailVal)) {
      submitErrors.email = 'E-mail inválido';
    }

    if (!phoneVal) {
      submitErrors.phone = 'Celular obrigatório';
    } else if (!validatePhone(phoneVal)) {
      submitErrors.phone = 'Celular inválido';
    }

    if (submitErrors.email || submitErrors.phone) {
      setFieldErrors(submitErrors);
      return;
    }

    if (!getIsValidForm() || isLoading) return;

    setIsLoading(true);

    try {
      const cleanPlate = (user?.plate as string)?.replace(/[^A-Z0-9]/g, "") || "";

      if (skipCaptchaValidation) {
        dispatch(
          authorize({
            plate: cleanPlate,
            token: '',
            callback: handleAuthorize,
            isAcceptedTerms: true,
          })
        );
        return;
      }
      
      if (isCaptchaMtcCaptcha) {
        // @ts-expect-error
        const mtcaptchaToken = window?.mtcaptcha?.getVerifiedToken?.(captchaName);
        // @ts-expect-error
        const mtcaptchaStatus = window?.mtcaptcha?.getStatus?.(captchaName);
        
        
        if (!mtcaptchaToken || !mtcaptchaStatus?.isVerified) {
          setError(true);
          toggleModal();
          setIsLoading(false);
          resetCaptcha();
          return;
        }

        dispatch(
          authorize({
            plate: cleanPlate,
            token: mtcaptchaToken,
            callback: handleAuthorize,
            isAcceptedTerms: true,
          })
        );
      } else {
        const recaptchaToken = (window as any)?.recaptchaToken;
        
        if (!recaptchaToken) {
          if ((window as any).executeRecaptcha) {
            (window as any).executeRecaptcha();
          }
          setError(true);
          toggleModal();
          setIsLoading(false);
          setIsCaptchaVerified(false);
          return;
        }

        dispatch(
          authorize({
            plate: cleanPlate,
            token: recaptchaToken,
            callback: handleAuthorize,
            recaptchaAction: !isCaptchaMtcCaptcha && (window as any).getRecaptchaAction ? (window as any).getRecaptchaAction() : undefined,
            isAcceptedTerms: true,
          })
        );
      }
    } catch (error) {
      setIsLoading(false);
    }
  }

  useLayoutEffect(() => {
    const hash = location.hash;
    
    let hasValidHash = false;
    if (hash && (hash.includes('#siga-facil') || hash.includes('#zapay')) && hash.includes('?')) {
      const queryString = hash.split('?')[1];
      const hashParams = new URLSearchParams(queryString);
      hasValidHash = !!hashParams.get("q");
    }
    
    if (!hasValidHash) {
      dispatch(setUser({ plate: '', company: undefined, nrc: undefined, accountId: undefined }));
      dispatch(setCustomer({
        register: undefined,
        forgot: undefined,
        customer: undefined,
        products: undefined,
        client: undefined,
        vehicles: undefined,
        extract: undefined,
        recharges: undefined,
        isLoadingVehicles: false,
        tickets: undefined,
        debts: undefined,
      }));
      dispatch(clearUser());
    }
    
    if (params.get("auth")) {
      toggleModalLogin();
    }
  }, []);


  useEffect(() => {
    const hash = location.hash;
    if (hash && hash.includes('#siga-facil')) {
      setThemeInStorage('siga-facil');

      let hashFromUrl: string | null = null;
      if (hash.includes('?')) {
        const queryString = hash.split('?')[1];
        const params = new URLSearchParams(queryString);
        hashFromUrl = params.get("q");
      }
      
      if (hashFromUrl && hashFromUrl !== lastProcessedHashFromUrl.current) {
        lastProcessedHashFromUrl.current = hashFromUrl;
        setIsLoading(true);
        
        dispatch(setUser({ 
          debts: [],
          cart: undefined,
          requestId: undefined,
          plate: '',
        }));
        dispatch(setCustomer({
          debts: undefined,
          cart: undefined,
          requestId: undefined,
        }));
        
        dispatch(setUser({ company: 'sigafacil' }));
        
        dispatch(
          checkLicensePlateHash({
            hash: hashFromUrl,
            company: 'sigafacil',
            callback: (success: boolean, plate?: string) => {
              setIsLoading(false);
              if (success && plate) {
                setTimeout(() => {
                  searchPlateFromUrl(plate);
                }, 100);
              } else {
                setError(true);
                toggleModal();
              }
            },
          })
        );
      } else if (!hashFromUrl) {
        lastProcessedHashFromUrl.current = null;
      }
    } else if (hash && hash.includes('#zapay')) {
      setThemeInStorage('zapay');

      let hashFromUrl: string | null = null;
      let nrc: string | null = null;
      let accountId: string | null = null;
      
      if (hash.includes('?')) {
        const queryString = hash.split('?')[1];
        const params = new URLSearchParams(queryString);
        hashFromUrl = params.get("q");
        nrc = params.get("nrc");
        accountId = params.get("accountId");
      }
      
      if (hashFromUrl && hashFromUrl !== lastProcessedHashFromUrl.current) {
        lastProcessedHashFromUrl.current = hashFromUrl;
        setIsLoading(true);
        
        dispatch(setUser({ 
          debts: [],
          cart: undefined,
          requestId: undefined,
          plate: '',
        }));
        dispatch(setCustomer({
          debts: undefined,
          cart: undefined,
          requestId: undefined,
        }));
        
        // Define company como zapay e armazena nrc e accountId
        dispatch(setUser({ 
          company: 'zapay',
          nrc: nrc || undefined,
          accountId: accountId || undefined
        }));
        
        dispatch(
          checkLicensePlateHash({
            hash: hashFromUrl,
            company: 'zapay',
            callback: (success: boolean, plate?: string) => {
              setIsLoading(false);
              if (success && plate) {
                setTimeout(() => {
                  searchPlateFromUrl(plate);
                }, 100);
              } else {
                setError(true);
                toggleModal();
              }
            },
          })
        );
      } else if (!hashFromUrl) {
        lastProcessedHashFromUrl.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  return (
    <IndexView
        toggleMenu={toggleMenu}
        isVisibleMenu={isVisibleMenu}
        user={user}
        onChange={onChange}
        goToDebts={goToDebts}
        isDisabled={!getIsValidForm()}
        isLoading={isLoading}
        error={error}
        isVisibleModal={isVisibleModal}
        toggleModal={toggleModal}
        isVisibleModalLogin={isVisibleModalLogin}
        toggleModalLogin={toggleModalLogin}
        goToCreateAccount={goToCreateAccount}
        isCaptchaVerified={isCaptchaVerified}
        isCaptchaMtcCaptcha={isCaptchaMtcCaptcha}
        skipCaptchaValidation={skipCaptchaValidation}
        isLoadingCaptchaFlag={isLoadingCaptchaFlag}
        isWebView={isWebView}
        errors={fieldErrors}
      />
  );
}
